export const getNormalizedPath = (pathname: string | undefined | null): string => {
  const raw = pathname || '/dashboard';
  return raw.endsWith('/') && raw !== '/' ? raw.slice(0, -1) : raw;
};

export const getTopLevelPath = (pathname: string | undefined | null): string => {
  const normalized = getNormalizedPath(pathname);
  return '/' + (normalized.split('/')[1] || 'dashboard');
};

/**
 * Profile area includes:
 * - /profile (main profile page)
 * - /settings (user settings)
 * - /referral (referral program)
 * - /premium-subscription (premium features - accessed from profile)
 * - /payment-history (payment records - accessed from profile)
 * - /payment-confirmation (payment success - accessed from profile)
 * - /gems (gems purchase - accessed from profile)
 * - /report-history (user reports - accessed from profile)
 */
export const isProfileAreaPath = (pathname: string | undefined | null): boolean => {
  const normalized = getNormalizedPath(pathname);
  const top = getTopLevelPath(normalized);
  return (
    top === '/profile' || 
    top === '/settings' || 
    top === '/referral' || 
    top === '/premium-subscription' ||
    top === '/payment-history' ||
    top === '/payment-confirmation' ||
    top === '/gems' ||
    top === '/report-history'
  );
};

/**
 * Library area includes:
 * - /library (main library page and all sub-routes)
 * - /learning (learning related pages)
 * - /learning/path (learning path page)
 * - /learning/quests (daily quests)
 * - /learning/challenges (challenges page)
 * - /learning/achievement (achievement page)
 * - /saved-books (saved books collection)
 * - /quests (daily quests - alternative path)
 * - /achievements (achievements page)
 * - /read (book reading page)
 * - /novel-read (novel reading page)
 * - /review (chapter review)
 * - /review/result (review results)
 */
export const isLibraryAreaPath = (pathname: string | undefined | null): boolean => {
  const normalized = getNormalizedPath(pathname);
  const top = getTopLevelPath(normalized);
  return (
    top === '/library' || 
    top === '/learning' || 
    top === '/saved-books' ||
    top === '/quests' ||
    top === '/achievements' ||
    top === '/read' ||
    top === '/novel-read' ||
    top === '/review' ||
    normalized.startsWith('/library/') ||
    normalized.startsWith('/learning/') ||
    normalized.startsWith('/review/')
  );
};

/**
 * League area includes:
 * - /league (main league page)
 */
export const isLeagueAreaPath = (pathname: string | undefined | null): boolean => {
  const normalized = getNormalizedPath(pathname);
  const top = getTopLevelPath(normalized);
  return top === '/league';
};

/**
 * Dashboard/Home area includes:
 * - /dashboard (main dashboard)
 * - /author/:author (author books page - accessed from dashboard)
 * - /tag/:tagId (tag books page - accessed from dashboard)
 */
export const isDashboardAreaPath = (pathname: string | undefined | null): boolean => {
  const normalized = getNormalizedPath(pathname);
  const top = getTopLevelPath(normalized);
  return (
    top === '/dashboard' ||
    top === '/author' ||
    top === '/tag'
  );
};

